// Lokale IndexedDB: Geraete-Identitaet + Offline-Outbox (Architektur §2
// "Offline-Storage-Strategie" -- ein Stempel ist ein lokal abgeschlossenes Ereignis,
// die Bestaetigung braucht keine Netzwerkantwort).
//
// Wichtig: `CryptoKey`-Objekte sind strukturiert klonbar und duerfen direkt in IndexedDB
// gespeichert werden -- das ist der Standardweg, einen nicht exportierbaren
// (`extractable: false`) privaten Schluessel ueber Reloads hinweg zu behalten, ohne ihn je
// als Bytes zu sehen.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { LogType } from './canonicalMessage'

export interface DeviceRecord {
  id: 'self'
  deviceId: string
  tenantSlug: string
  privateKey: CryptoKey
  publicKeyRaw: string // Base64, SEC1 unkomprimiert -- gecacht, damit wir nicht bei jedem
  // Checkin neu exportieren muessen
  monotonicCounter: number
}

export type OutboxStatus = 'pending' | 'confirmed'

export interface OutboxEntry {
  eventId: string
  deviceId: string
  tenantSlug: string
  employeeIdentifier: string
  logType: LogType
  clientTimeIso: string
  monotonicCounter: number
  signature: string // Base64, r||s
  status: OutboxStatus
  createdAt: string
  attempts: number
  lastAttemptAt: string | null
  lastError: string | null
  // Rapport-Digitalisierung (TQ #18899/#18900) -- alle optional, ein normaler Kommen/Gehen-
  // Checkin ohne Auftragsbezug bleibt moeglich (Stempel-App-Kernfunktion aendert sich nicht).
  auftragId?: string
  taetigkeitGeplant?: string
  taetigkeitIst?: string
  zusatzgeraet?: string
}

export type Gewerk = 'erdbau' | 'abbruch' | 'transport' | 'sonstiges'
export type AuftragStatus = 'offen' | 'abgeschlossen'

/** Vom Server gespiegelte Auftrags-Stammdaten, offline verfuegbar (analog Architektur §2,
 * EC-04 Baustellenwechsel) -- kompletter Ersatz bei jedem erfolgreichen Sync, kein Merge. */
export interface CachedAuftrag {
  auftragId: string
  auftragsnummer: string
  auftraggeberName: string
  auftragszeitraumVon: string | null
  auftragszeitraumBis: string | null
  auftragsort: string | null
  gewerk: Gewerk
  status: AuftragStatus
}

export type PersonalOutboxStatus = 'pending' | 'sent' | 'konflikt' | 'rejected'

/** Persoenlicher Stempel (/me/checkins) im Offline-Puffer (TQ #556). Die eventId ist der
 * Idempotenz-Anker gegenueber der API, clientTimeIso die Zeit beim Tippen -- beide werden
 * einmal erzeugt und bei jeder Wiederholung unveraendert gesendet. */
export interface PersonalOutboxEntry {
  eventId: string
  employeeIdentifier: string
  logType: LogType
  clientTimeIso: string
  status: PersonalOutboxStatus
  createdAt: string
  attempts: number
  lastAttemptAt: string | null
  lastError: string | null
  konfliktGrund: string | null
  // true, wenn die Antwort unsere eventId nicht zurueckgespiegelt hat (aelterer Server ohne
  // event_id-Unterstuetzung): der Stempel ist angenommen, aber nicht idempotent abgesichert.
  serverIgnoredEventId: boolean
  auftragId?: string
  taetigkeitGeplant?: string
  taetigkeitIst?: string
  zusatzgeraet?: string
}

interface StempelDB extends DBSchema {
  personalOutbox: {
    key: string // eventId
    value: PersonalOutboxEntry
    indexes: { 'by-status': PersonalOutboxStatus }
  }
  device: {
    key: 'self'
    value: DeviceRecord
  }
  outbox: {
    key: string // eventId
    value: OutboxEntry
    indexes: { 'by-status': OutboxStatus }
  }
  auftraege: {
    key: string // auftragId
    value: CachedAuftrag
  }
}

let dbPromise: Promise<IDBPDatabase<StempelDB>> | null = null

export function getDb(): Promise<IDBPDatabase<StempelDB>> {
  if (!dbPromise) {
    dbPromise = openDB<StempelDB>('stempel-pwa', 3, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('device', { keyPath: 'id' })
          const outbox = db.createObjectStore('outbox', { keyPath: 'eventId' })
          outbox.createIndex('by-status', 'status')
        }
        if (oldVersion < 2) {
          db.createObjectStore('auftraege', { keyPath: 'auftragId' })
        }
        if (oldVersion < 3) {
          const personal = db.createObjectStore('personalOutbox', { keyPath: 'eventId' })
          personal.createIndex('by-status', 'status')
        }
      },
    })
  }
  return dbPromise
}

/** Ersetzt den gesamten lokalen Auftrags-Cache durch den aktuellen Server-Stand (nur offene
 * Aufträge werden je synchronisiert, siehe lib/auftrag.ts) -- kein inkrementelles Merge, ein
 * bei einem anderen Geraet abgeschlossener Auftrag muss aus der lokalen Liste verschwinden. */
export async function replaceCachedAuftraege(auftraege: CachedAuftrag[]): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('auftraege', 'readwrite')
  await tx.store.clear()
  await Promise.all(auftraege.map((a) => tx.store.put(a)))
  await tx.done
}

export async function getCachedAuftraege(): Promise<CachedAuftrag[]> {
  const db = await getDb()
  return db.getAll('auftraege')
}

export async function getDevice(): Promise<DeviceRecord | undefined> {
  const db = await getDb()
  return db.get('device', 'self')
}

export async function saveDevice(record: DeviceRecord): Promise<void> {
  const db = await getDb()
  await db.put('device', record)
}

/** Naechster monotoner Zaehlerwert -- rein lokale Ordnungs-Absicherung bei Uhr-Drift
 * (Architektur §2), kein Ersatz fuer den event_id-Idempotenz-Anker. */
export async function nextMonotonicCounter(): Promise<number> {
  const device = await getDevice()
  if (!device) throw new Error('Kein Geraet enrollt')
  const next = device.monotonicCounter + 1
  await saveDevice({ ...device, monotonicCounter: next })
  return next
}

export async function enqueueOutboxEntry(
  entry: Omit<OutboxEntry, 'status' | 'attempts' | 'lastAttemptAt' | 'lastError'>,
): Promise<void> {
  const db = await getDb()
  await db.put('outbox', {
    ...entry,
    status: 'pending',
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
  })
}

export async function getPendingEntries(): Promise<OutboxEntry[]> {
  const db = await getDb()
  return db.getAllFromIndex('outbox', 'by-status', 'pending')
}

export async function countPending(): Promise<number> {
  const db = await getDb()
  return db.countFromIndex('outbox', 'by-status', 'pending')
}

export async function markConfirmed(eventId: string): Promise<void> {
  const db = await getDb()
  const entry = await db.get('outbox', eventId)
  if (!entry) return
  await db.put('outbox', { ...entry, status: 'confirmed' })
}

export async function recordAttempt(eventId: string, error: string | null): Promise<void> {
  const db = await getDb()
  const entry = await db.get('outbox', eventId)
  if (!entry) return
  await db.put('outbox', {
    ...entry,
    attempts: entry.attempts + 1,
    lastAttemptAt: new Date().toISOString(),
    lastError: error,
  })
}

// --- Persoenlicher Offline-Puffer (TQ #556) ---

export type NewPersonalOutboxEntry = Omit<
  PersonalOutboxEntry,
  'status' | 'attempts' | 'lastAttemptAt' | 'lastError' | 'konfliktGrund' | 'serverIgnoredEventId'
>

export async function enqueuePersonalEntry(entry: NewPersonalOutboxEntry): Promise<PersonalOutboxEntry> {
  const db = await getDb()
  const full: PersonalOutboxEntry = {
    ...entry,
    status: 'pending',
    attempts: 0,
    lastAttemptAt: null,
    lastError: null,
    konfliktGrund: null,
    serverIgnoredEventId: false,
  }
  await db.put('personalOutbox', full)
  return full
}

export async function getPersonalEntries(): Promise<PersonalOutboxEntry[]> {
  const db = await getDb()
  return db.getAll('personalOutbox')
}

export async function getPersonalPending(): Promise<PersonalOutboxEntry[]> {
  const db = await getDb()
  return db.getAllFromIndex('personalOutbox', 'by-status', 'pending')
}

export async function countPersonalPending(): Promise<number> {
  const db = await getDb()
  return db.countFromIndex('personalOutbox', 'by-status', 'pending')
}

/** Aendert nur die uebergebenen Felder des Eintrags (Lesen+Schreiben in einer Transaktion). */
export async function updatePersonalEntry(
  eventId: string,
  patch: Partial<PersonalOutboxEntry>,
): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('personalOutbox', 'readwrite')
  const entry = await tx.store.get(eventId)
  if (entry) await tx.store.put({ ...entry, ...patch, eventId })
  await tx.done
}

export async function deletePersonalEntries(eventIds: string[]): Promise<void> {
  const db = await getDb()
  const tx = db.transaction('personalOutbox', 'readwrite')
  await Promise.all(eventIds.map((id) => tx.store.delete(id)))
  await tx.done
}
